import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { getInfoGraphics } from '../../Services/ApiCalls';
import { Album } from '../../types';

// Async thunk to fetch all albums
export const fetchAllAlbums = createAsyncThunk(
    'albums/fetchAll',
    async (_, { rejectWithValue }) => {
        try {
            // Fetch a large batch to get all albums
            const response = await getInfoGraphics(1, 1000);
            return response.albums || [];
        } catch (error: any) {
            return rejectWithValue(error.message || 'Failed to fetch albums');
        }
    }
);

type AlbumsState = {
    albums: Album[];
    loading: boolean;
    error: string | null;
    lastFetched: number | null; // timestamp of last successful fetch
};

const initialState: AlbumsState = {
    albums: [],
    loading: false,
    error: null,
    lastFetched: null,
};

const albumsSlice = createSlice({
    name: 'albums',
    initialState,
    reducers: {
        clearAlbums: (state) => {
            state.albums = [];
            state.lastFetched = null;
        },
        updateAlbumLike: (state, action: PayloadAction<{ id: number; likes: number }>) => {
            const album = state.albums.find(a => a.id === action.payload.id);
            if (album) {
                // Ensure likes is always a number, not null/undefined
                album.like = action.payload.likes === null || action.payload.likes === undefined ? 0 : action.payload.likes;
            }
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchAllAlbums.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchAllAlbums.fulfilled, (state, action: PayloadAction<Album[]>) => {
                state.loading = false;
                // Normalize like values: convert null/undefined to 0
                state.albums = action.payload.map(album => ({
                    ...album,
                    like: album.like === null || album.like === undefined ? 0 : album.like
                }));
                state.error = null;
                state.lastFetched = Date.now();
            })
            .addCase(fetchAllAlbums.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload as string;
            });
    },
});

export const { clearAlbums, updateAlbumLike } = albumsSlice.actions;
export default albumsSlice.reducer;

